import { useEffect, useState } from 'react';
import { getFinanceSummary, getFinanceTrend } from '../../api/invoicing.api';
import { FinanceSummaryDto, FinanceTrendDto } from '../../common/interfaces/invoicing.interface';
import { parseMonthKey } from '../../utilities/helper/date.helper';

/**
 * How far back the trend chart looks. Six months, not a year: it sits in a half-width card
 * beside the breakdown chart, and twelve pairs of bars there are too thin to read.
 */
const TREND_MONTHS = 6;

interface FinanceScopeArgs {
  kindergartenId: string;
  /** `YYYY-MM`. */
  month: string;
  locationId?: string;
  groupId?: string;
  /** The overview looks a year back; the drill-down pages, half that. */
  trendMonths?: number;
  /** Bumped to fetch again, after something on the page changed the money. */
  reloadKey?: number;
}

/**
 * The figures for one scope and month, plus the six months behind them.
 *
 * Both requests are fired together: they answer the same question at two time scales, and
 * loading them in sequence makes the tiles settle and then the chart jump.
 */
export const useFinanceSummary = ({
  kindergartenId,
  month,
  locationId,
  groupId,
  trendMonths = TREND_MONTHS,
  reloadKey = 0
}: FinanceScopeArgs) => {
  const [summary, setSummary] = useState<FinanceSummaryDto | null>(null);
  const [trend, setTrend] = useState<FinanceTrendDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const parsed = parseMonthKey(month);
    if (!kindergartenId || !parsed) {
      setSummary(null);
      setTrend(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setFailed(false);

    const params = { ...parsed, locationId, groupId };

    Promise.all([getFinanceSummary(kindergartenId, params), getFinanceTrend(kindergartenId, { ...params, months: trendMonths })])
      .then(([summaryResult, trendResult]) => {
        if (cancelled) return;
        setSummary(summaryResult.results ?? null);
        setTrend(trendResult.results ?? null);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [kindergartenId, month, locationId, groupId, trendMonths, reloadKey]);

  return { summary, trend, isLoading, failed };
};
